import React, { useContext, useCallback, useMemo } from 'react';
import './SelectElementRegistrationEditor.less';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { IEntityPickerProps } from './EditorEntityPicker';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { useEditorId } from '../hooks/useEditorId';
import { getPropertyDisplayLabel, IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { EditorLabel } from '../EditorLabel';
import { EditorConfigButtons } from '../EditorConfigButtons';
import { ValidationWrapper } from 'components/inputs/Validation/ValidationWrapper';
import { GridPopup } from 'components/property-editors/PropertyGrid/GridPopup';
import { useEpickEditor } from './useEntityPickerEditor';
import { getEditorConfigValue, getEditorSettings } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EntityPickerWithLinks } from './EntityPickerWithLinks';

/**
 * Select Element Registration Editor.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function SelectElementRegistrationEditor(props: IEntityPickerProps) {
  const selectRegistrationProps = {
    ...props,
    canCreate: true,
    canEdit: true,
  };
  const { child, path, hideInputLabel } = selectRegistrationProps;
  const context = useContext(PropertyEditorContext);
  const [id] = useEditorId(path);
  const validation = getEditorValidationSummary(child);

  const { showEdit, showCreate, showPick, showReset, entityName, onValueChange } = useEpickEditor(
    selectRegistrationProps
  );

  const showEditor = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      name: 'ShowEditor',
      editingContextId: context.editingContextId,
    });
  }, [child, context, path]);

  const editOnly: boolean = showEdit && !showCreate && !showPick;
  const elementName: string = getPropertyDisplayLabel(child);
  const showEditorWithLinks: boolean = getEntityPickerStyle(child);

  const showPickOrCreateDialog = useCallback(
    (canCreate: boolean, canPick: boolean) => {
      context.onShowDialogue({
        node: child,
        path,
        editingContextId: context.editingContextId,
        name: 'ShowPickerOrCreator',
        options: {
          onCreated: onValueChange,
          onPicked: (registrationId: string) => {
            onValueChange(registrationId);
          },
          entityPickerSettings: getEditorSettings(child),
          onRefresh: () => {
            context.refreshContext();
          },
          showCreate: canCreate,
          showPick: canPick,
        },
      });
    },
    [child, context, onValueChange, path]
  );

  const showPicker = useCallback(() => {
    showPickOrCreateDialog(false, true);
  }, [showPickOrCreateDialog]);

  const showCreator = useCallback(() => {
    showPickOrCreateDialog(true, false);
  }, [showPickOrCreateDialog]);

  const showPickerAndCreator = useCallback(() => {
    showPickOrCreateDialog(showCreate, showPick);
  }, [showCreate, showPick, showPickOrCreateDialog]);

  const drop = useCallback(() => {
    onValueChange(null);
  }, [onValueChange]);

  const [testId] = useEditorId(path);

  const buttonText = useMemo(() => {
    if (showEdit) {
      return 'Edit';
    } else if (showPick && showCreate) {
      return 'Pick or Create';
    } else if (showPick) {
      return 'Pick';
    } else if (showCreate) {
      return 'Create';
    }
    return '';
  }, [showCreate, showEdit, showPick]);

  //old style with Create/Pick links
  if (showEditorWithLinks) {
    return (
      <EntityPickerWithLinks
        editor={child}
        path={path}
        hideInputLabel={hideInputLabel}
        onDrop={showReset ? drop : undefined}
        onCreateClick={showCreate ? showCreator : undefined}
        onPickClick={showPick ? showPicker : undefined}
        onEditClick={showEdit ? showEditor : undefined}
        entityName={entityName}
      />
    );
  } else {
    return (
      <EditorLabel htmlFor={id} editor={child} path={path} doNotWrap hideInputLabel={true}>
        <div className="dpc-editor-epick">
          <EditorConfigButtons>
            <ValidationWrapper validation={validation}>
              <button
                className="dp-button-outline full-size"
                onClick={showEdit === false ? showPickerAndCreator : showEditor}
                title={editOnly ? entityName : elementName}
              >
                {/* Big button decides to show Entity/Element Name when Edit Only */}
                {buttonText} {editOnly ? entityName : elementName}
              </button>
            </ValidationWrapper>
          </EditorConfigButtons>
          {!!entityName?.length && !editOnly && (
            <GridPopup
              id={id}
              label={entityName}
              value={entityName}
              onClear={showReset ? drop : () => {}}
              onOpen={() => {}}
              disableOpen={true}
              disableClear={!showReset}
              placeholder={entityName}
              hideLabel={true}
              data-testid={testId}
            >
              {entityName}
            </GridPopup>
          )}
        </div>
      </EditorLabel>
    );
  }
}

export function getEntityPickerStyle(child: IPropertyChild): boolean {
  const result = getEditorConfigValue(child, 'ShowEditorWithLinks') as boolean;

  return result;
}
